<template>
  <template v-if="brand === 'bcom' && mySizesEnabled">
    <div class="my-sizes-container">
      <span class="my-sizes-text">MY SIZES</span>
      <button
        v-tooltip.bottom="
          'Enabling My Sizes automatically filters your saved sizes (for women and/or men) while you shop.'
        "
        class="tooltip-small"
      />
      <Toggle
        v-model="toggleState"
        @change="onToggleChange"
      />
    </div>
  </template>

  <template v-if="dropdownOptions?.length">
    <div :class="['sizes-dropdown', sizeDropdownExp ]">
      <Form>
        <Dropdown
          v-if="isReady"
          v-model="selectedOption"
          label="Size Type"
          :options="dropdownOptions"
          @change="onDropdownChange"
        />
      </Form>
    </div>
  </template>

  <MultiSelectDefault
    v-if="filteredFacet && isReady"
    :facet="filteredFacet"
    :parent-facet="sizeFacet"
    :custom-value-renderer="customValueRenderer"
    :update-selected="onUpdate"
    :is-dynamic="isDynamic"
  />
</template>

<script setup>
import Form from '@atomic-ui/form';
import Dropdown from '@atomic-ui/dropdown';
import Toggle from '@atomic-ui/toggle';
import vTooltip from '@atomic-ui/tooltip';
import { brand } from 'config';
import MultiSelectDefault from './MultiSelectDefault';
import useSizeFacet from '../../composables/useSizeFacet';
import useMySizes from '../../composables/useMySizes';

const props = defineProps({
  facet: {
    type: Object,
    required: true,
  },
  isDynamic: {
    type: Boolean,
    default: () => false,
  },
});

const {
  dropdownOptions,
  selectedOption,
  filteredFacet,
  onDropdownChange,
  customValueRenderer,
  onUpdate,
  onToggleChange,
  toggleState,
  sizeDropdownExp,
  isReady,
  sizeFacet,
} = useSizeFacet(props);

const { mySizesEnabled } = useMySizes();
</script>

// MCOM Styles
<style scoped lang="scss" brand="mcom">
:deep(.checkbox-container) {
  .size-value-container {
    border: 1px solid #626369;
    border-radius: 5px;

    .text {
      line-height: 20px;
    }

    .sub-text {
      line-height: 18px;
      color: #626369;
    }
  }
}

:deep(.select-container select) {
  padding-top: 10px;
  padding-bottom: 10px;
}
</style>

// BCOM Styles
<style scoped lang="scss" brand="bcom">
@import "@csgIcons/_tooltip-small.scss";

:deep(.checkbox-container) {
  .size-value-container {
    border: 1px solid #6e6e6e;
    font-weight: 300;

    .text {
      line-height: 18px;
    }

    .sub-text {
      line-height: 16px;
      color: #6e6e6e;
    }
  }
}

:deep(.select-container select) {
  padding-top: 11px;
  padding-bottom: 11px;
}

.my-sizes-container {
  display: flex;
  align-items: center;

  .my-sizes-text {
    font-weight: 500;
    margin-right: 5px;
    @media only screen and (max-width: 1023px) {
      margin-right: 11px;
    }
  }

  .tooltip-small {
    margin-right: 16px;
    @media only screen and (max-width: 1023px) {
      margin-right: 0;
    }
  }

  .p-inputswitch {
    @media only screen and (max-width: 1023px) {
      margin-left: auto;
    }
  }
}
</style>

// Common Styles
<style scoped lang="scss">
:deep(.checkBoxesContainer) {
  --size-facet-m-r: calc(#{var(--filtering-facets-scrollbar-width)} * -1);
  margin-right: var(--size-facet-m-r);
}

.sizes-dropdown {
  width: 95%;
}

.size-dropdown-full-width {
  width: 100%;
}

:deep(.select-container) {
  margin-bottom: 24px;
  margin-top: 8px;
}

:deep(.checkbox-container) {
  .checkbox-wrapper {
    display: inline-flex;
    vertical-align: middle;
    margin-bottom: 0 !important;
    position: relative;
    overflow: hidden;

    .title {
      width: 500px;
      margin: 24px 0 16px;
    }

    label {
      position: relative;
      z-index: 2;
    }

    input[name="criteria-checkbox"] {
      opacity: 0;
      position: absolute;
      z-index: -1;

      &:focus + label .size-value-container {
        outline: 1px solid black;
      }

      &:checked + label .size-value-container {
        border: 2px solid black;
      }
    }

    .size-value-container {
      width: 83px;
      white-space: normal;

      @media only screen and (max-width: 1023px) {
        width: 72px;
        min-width: 72px;
      }
      height: 50px;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      text-align: center;

      .sub-text {
        font-size: 12px;
      }
      margin: 0 16px 16px 0;
      @media only screen and (max-width: 1023px) {
        margin: 0 14px 24px 0;
      }
    }

    @media (hover: hover) {
      .size-value-container:hover {
        border: 2px solid #000;
      }
    }
  }

  overflow: hidden;
}
</style>

<style lang="scss">
.optimazed-facets {
  .checkbox-container .checkbox-wrapper .size-value-container {
    @media only screen and (min-width: 1024px) and (max-width: 1280px) {
      width: rem-calc(78);
      min-width: rem-calc(78);
    }
  }
}
</style>
